import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Search, Filter, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import PatternVisualizer from "../components/PatternVisualizer";

const BingoPatternsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [patterns, setPatterns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pattern categories
  const categories = {
    all: "All Patterns",
    classic: "Classic Patterns",
    letters: "Letter Patterns",
    geometric: "Geometric Shapes",
    special: "Special Patterns",
    themed: "Themed Patterns",
  };

  // Pattern categorization
  const patternCategories = {
    classic: ["classic", "fourCorners", "blackout"],
    letters: [
      "letterX",
      "letterL",
      "letterT",
      "letterH",
      "letterZ",
      "letterN",
      "letterU",
      "letterO",
      "letterY",
      "letterE",
      "letterF",
    ],
    geometric: [
      "plus",
      "diamond",
      "centerSquare",
      "corners2x2",
      "frame",
      "pyramid",
      "hourglass",
      "arrow",
      "cross",
      "doubleX",
    ],
    special: [
      "postage",
      "checkmark",
      "heart",
      "target",
      "windmill",
      "kite",
      "stamp",
    ],
    themed: ["dice", "crown", "star", "rainbowArch", "cup"],
  };

  // Get all the possible pattern types from the game
  useEffect(() => {
    // All patterns grouped by category for better organization
    const allPatterns = [
      // Classic patterns
      {
        id: "classic",
        name: "Classic Bingo",
        description:
          "Complete any row, column, or diagonal on the card. This is the most traditional way to play bingo.",
        icon: "🎯",
        difficulty: "Easy",
        category: "classic",
        popularity: "High",
      },
      {
        id: "fourCorners",
        name: "Four Corners",
        description:
          "Mark the four corners of the bingo card. A simple but fun pattern that's easy for beginners.",
        icon: "◢◣◥◤",
        difficulty: "Easy",
        category: "classic",
        popularity: "High",
      },
      {
        id: "blackout",
        name: "Blackout",
        description:
          "Cover every number on the entire card. Also known as 'Full House' or 'Cover All', this is the most challenging pattern.",
        icon: "⬛",
        difficulty: "Hard",
        category: "classic",
        popularity: "High",
      },

      // Letter patterns
      {
        id: "letterX",
        name: "Letter X",
        description: "Mark both diagonals to form an X shape across your card.",
        icon: "❌",
        difficulty: "Medium",
        category: "letters",
        popularity: "High",
      },
      {
        id: "letterL",
        name: "Letter L",
        description:
          "Complete the leftmost column and bottom row to form an L shape.",
        icon: "⅃",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterT",
        name: "Letter T",
        description:
          "Complete the top row and middle column to form a T shape.",
        icon: "T",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterH",
        name: "Letter H",
        description:
          "Complete both outer columns and the middle row to form an H shape.",
        icon: "H",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterZ",
        name: "Letter Z",
        description:
          "Mark the top row, a diagonal line down, and the bottom row to form a Z shape.",
        icon: "Z",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterN",
        name: "Letter N",
        description:
          "Complete the left column, a diagonal line, and the right column to form an N shape.",
        icon: "N",
        difficulty: "Medium",
        category: "letters",
        popularity: "Low",
      },
      {
        id: "letterU",
        name: "Letter U",
        description:
          "Mark both outer columns and the bottom row to create a U shape.",
        icon: "U",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterO",
        name: "Letter O",
        description:
          "Complete the outer frame without corners to create an O shape.",
        icon: "O",
        difficulty: "Medium",
        category: "letters",
        popularity: "Medium",
      },
      {
        id: "letterY",
        name: "Letter Y",
        description:
          "Form a Y shape starting from the top row and extending down through the center.",
        icon: "Y",
        difficulty: "Medium",
        category: "letters",
        popularity: "Low",
      },
      {
        id: "letterE",
        name: "Letter E",
        description:
          "Mark the left column plus the top, middle, and bottom rows.",
        icon: "E",
        difficulty: "Hard",
        category: "letters",
        popularity: "Low",
      },
      {
        id: "letterF",
        name: "Letter F",
        description: "Complete the left column plus the top and middle rows.",
        icon: "F",
        difficulty: "Medium",
        category: "letters",
        popularity: "Low",
      },

      // Geometric patterns
      {
        id: "plus",
        name: "Plus Sign",
        description:
          "Complete the middle row and middle column to form a plus or cross shape.",
        icon: "➕",
        difficulty: "Medium",
        category: "geometric",
        popularity: "Medium",
      },
      {
        id: "diamond",
        name: "Diamond",
        description: "Form a diamond pattern in the center of the card.",
        icon: "♦️",
        difficulty: "Medium",
        category: "geometric",
        popularity: "Medium",
      },
      {
        id: "centerSquare",
        name: "Center Square",
        description:
          "Complete the 3×3 center square in the middle of the card.",
        icon: "⬚",
        difficulty: "Medium",
        category: "geometric",
        popularity: "Medium",
      },
      {
        id: "corners2x2",
        name: "Four 2×2 Corners",
        description: "Mark 2×2 squares in all four corners of the bingo card.",
        icon: "◱◲◳◰",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Low",
      },
      {
        id: "frame",
        name: "Picture Frame",
        description:
          "Complete the outside frame of the card, marking all border numbers.",
        icon: "🖼️",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Medium",
      },
      {
        id: "pyramid",
        name: "Pyramid",
        description:
          "Form a pyramid shape from the bottom row up to a single number at the top.",
        icon: "🔺",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Low",
      },
      {
        id: "hourglass",
        name: "Hourglass",
        description:
          "Complete the top and bottom rows with a narrow middle to form an hourglass shape.",
        icon: "⌛",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Low",
      },
      {
        id: "arrow",
        name: "Arrow",
        description: "Form an arrow shape pointing upward on the card.",
        icon: "⬆️",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Low",
      },
      {
        id: "cross",
        name: "Cross",
        description:
          "Complete both middle row and column to form a large cross pattern.",
        icon: "✚",
        difficulty: "Medium",
        category: "geometric",
        popularity: "Medium",
      },
      {
        id: "doubleX",
        name: "Double X",
        description:
          "Combine the X pattern with the four corners for an advanced winning pattern.",
        icon: "✶",
        difficulty: "Hard",
        category: "geometric",
        popularity: "Low",
      },

      // Special patterns
      {
        id: "postage",
        name: "Postage Stamp",
        description: "Mark a 2×2 square in any corner of the bingo card.",
        icon: "🎫",
        difficulty: "Easy",
        category: "special",
        popularity: "Medium",
      },
      {
        id: "checkmark",
        name: "Checkmark",
        description:
          "Form a diagonal from bottom-left to middle, then right to create a checkmark shape.",
        icon: "✓",
        difficulty: "Medium",
        category: "special",
        popularity: "Low",
      },
      {
        id: "heart",
        name: "Heart Shape",
        description:
          "Create a heart shape on the bingo card - a romantic pattern for Valentine's games.",
        icon: "❤️",
        difficulty: "Hard",
        category: "special",
        popularity: "Low",
      },
      {
        id: "target",
        name: "Target",
        description:
          "Mark the center spot and all surrounding numbers to form a target pattern.",
        icon: "🎯",
        difficulty: "Medium",
        category: "special",
        popularity: "Low",
      },
      {
        id: "windmill",
        name: "Windmill",
        description:
          "Create a pattern with the center number and lines extending to each corner.",
        icon: "💨",
        difficulty: "Hard",
        category: "special",
        popularity: "Low",
      },
      {
        id: "kite",
        name: "Kite",
        description:
          "Form a kite shape from the center extending outward to the edges.",
        icon: "🪁",
        difficulty: "Hard",
        category: "special",
        popularity: "Low",
      },
      {
        id: "stamp",
        name: "Stamp",
        description:
          "Mark a small postage stamp pattern in each corner of the card.",
        icon: "📬",
        difficulty: "Hard",
        category: "special",
        popularity: "Low",
      },

      // Themed patterns
      {
        id: "dice",
        name: "Lucky Six",
        description: "Mark numbers arranged like the six dots on a die face.",
        icon: "🎲",
        difficulty: "Medium",
        category: "themed",
        popularity: "Low",
      },
      {
        id: "crown",
        name: "Crown",
        description:
          "Complete the top row with peaks descending into the card to form a royal crown shape.",
        icon: "👑",
        difficulty: "Medium",
        category: "themed",
        popularity: "Low",
      },
      {
        id: "star",
        name: "Star Pattern",
        description:
          "Form a star shape centered on the card for a celestial winning pattern.",
        icon: "⭐",
        difficulty: "Hard",
        category: "themed",
        popularity: "Medium",
      },
      {
        id: "rainbowArch",
        name: "Rainbow Arch",
        description:
          "Complete a rainbow-shaped arch at the top of your bingo card.",
        icon: "🌈",
        difficulty: "Medium",
        category: "themed",
        popularity: "Low",
      },
      {
        id: "cup",
        name: "Cup",
        description:
          "Create a trophy cup or goblet shape on your card to claim victory.",
        icon: "🏆",
        difficulty: "Medium",
        category: "themed",
        popularity: "Low",
      },
    ];

    setPatterns(allPatterns);
    setIsLoading(false);
  }, []);

  // Filter patterns based on search term and category
  const filteredPatterns = patterns.filter((pattern) => {
    const matchesSearch =
      pattern.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pattern.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filter === "all" || patternCategories[filter].includes(pattern.id);

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>Bingo Winning Patterns Guide | Call My Bingo</title>
        <meta
          name="description"
          content="Explore 35+ winning bingo patterns from classic lines to creative shapes. Find detailed explanations and visual examples for each pattern to enhance your bingo games."
        />
        <meta
          name="keywords"
          content="bingo patterns, winning bingo patterns, bingo game rules, bingo card patterns, blackout bingo, letter patterns bingo"
        />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center mb-6">
            <Link
              to="/"
              className="text-white hover:text-yellow-300 transition-colors flex items-center"
            >
              <ArrowLeft size={20} className="mr-2" />
              <span>Back to Game</span>
            </Link>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Bingo Winning Patterns
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Discover all 35+ winning patterns you can play in Call My Bingo,
            from classic lines to creative shapes.
          </p>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search Bar */}
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={20} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Search for patterns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filter Dropdown */}
          <div className="relative">
            <div className="flex items-center">
              <Filter size={20} className="text-gray-500 mr-2" />
              <select
                className="block w-48 py-3 px-4 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none cursor-pointer"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                {Object.entries(categories).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {/* Statistics */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Bingo Pattern Stats
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">
                    Total Patterns
                  </p>
                  <p className="text-3xl font-bold text-purple-800">
                    {patterns.length}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">
                    Letter Patterns
                  </p>
                  <p className="text-3xl font-bold text-blue-800">
                    {patternCategories.letters.length}
                  </p>
                </div>
                <div className="bg-pink-50 p-4 rounded-lg">
                  <p className="text-sm text-pink-600 font-medium">
                    Geometric Shapes
                  </p>
                  <p className="text-3xl font-bold text-pink-800">
                    {patternCategories.geometric.length}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">
                    Special Patterns
                  </p>
                  <p className="text-3xl font-bold text-green-800">
                    {patternCategories.special.length +
                      patternCategories.themed.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Pattern Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPatterns.length > 0 ? (
                filteredPatterns.map((pattern) => (
                  <div
                    key={pattern.id}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center">
                          <div className="text-3xl mr-3">{pattern.icon}</div>
                          <h3 className="text-xl font-semibold text-gray-800">
                            {pattern.name}
                          </h3>
                        </div>
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            pattern.difficulty === "Easy"
                              ? "bg-green-100 text-green-800"
                              : pattern.difficulty === "Medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {pattern.difficulty}
                        </span>
                      </div>

                      <div className="flex items-center mb-4">
                        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                          <PatternVisualizer patternType={pattern.id} />
                        </div>
                        <p className="ml-4 text-sm text-gray-600">
                          {pattern.description}
                        </p>
                      </div>

                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span className="px-2 py-1 bg-gray-100 rounded-full">
                          {categories[pattern.category]}
                        </span>
                        <span className="flex items-center">
                          <span className="mr-1">Popularity:</span>
                          <span
                            className={`font-medium ${
                              pattern.popularity === "High"
                                ? "text-green-600"
                                : pattern.popularity === "Medium"
                                ? "text-yellow-600"
                                : "text-gray-600"
                            }`}
                          >
                            {pattern.popularity}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-3 text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No patterns found matching your search.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setFilter("all");
                    }}
                    className="mt-4 text-purple-600 hover:text-purple-800"
                  >
                    Reset filters
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* SEO Content */}
        <div className="mt-16 prose prose-purple max-w-none">
          <h2>Understanding Bingo Winning Patterns</h2>
          <p>
            Bingo is a game of chance where players mark numbers on their cards
            as they are called out. The first player to complete a predetermined
            pattern wins the game. While the traditional bingo game involves
            completing a horizontal, vertical, or diagonal line, modern bingo
            offers a variety of exciting patterns to keep the game fresh and
            engaging.
          </p>

          <h3>How Bingo Patterns Work</h3>
          <p>
            Before starting a bingo game, the caller announces which pattern
            players need to complete to win. Each pattern requires marking
            specific positions on the bingo card. Some patterns are simple and
            easy to achieve, like a straight line or four corners, while others
            are more complex and challenging, like blackout or intricate shapes.
          </p>

          <h3>Popular Bingo Pattern Categories</h3>

          <h4>Classic Patterns</h4>
          <p>
            These are the traditional patterns that most players are familiar
            with. They include single lines (horizontal, vertical, or diagonal),
            four corners, and blackout (covering the entire card).
          </p>

          <h4>Letter Patterns</h4>
          <p>
            These patterns form the shapes of letters on the bingo card. Common
            examples include X, L, T, N, H, F, E, O, U, Y, and Z. Letter
            patterns add a fun twist to the game while being visually
            recognizable.
          </p>

          <h4>Geometric Patterns</h4>
          <p>
            These patterns create geometric shapes on the card, such as
            diamonds, plus signs, squares, pyramids, and frames. They offer
            moderate to challenging difficulty depending on the complexity of
            the shape.
          </p>

          <h4>Special and Themed Patterns</h4>
          <p>
            These unique patterns include shapes like hearts, stars, crowns, and
            other creative designs. They're perfect for themed bingo nights or
            special occasions, adding an extra layer of excitement to your
            games.
          </p>

          <h3>Tips for Playing with Different Bingo Patterns</h3>
          <ul>
            <li>
              <strong>Understand the pattern before starting:</strong> Always
              make sure you know exactly what pattern is required to win.
            </li>
            <li>
              <strong>Mark your free space:</strong> In games with a free center
              space, mark it immediately as it counts toward your pattern.
            </li>
            <li>
              <strong>Use daubers strategically:</strong> For complex patterns,
              consider using different colored daubers to help visualize your
              progress.
            </li>
            <li>
              <strong>Start with easier patterns:</strong> If you're new to
              pattern bingo, begin with simpler patterns like lines or letters
              before attempting more complex shapes.
            </li>
          </ul>

          <h3>Create Your Own Bingo Game with Custom Patterns</h3>
          <p>
            Call My Bingo allows you to select from over 35 different winning
            patterns for your games. Whether you're hosting a small family game
            night or organizing a large community bingo event, you can keep
            things interesting by varying the patterns from game to game. Mix
            classic patterns with more challenging designs to create a dynamic
            and engaging bingo experience for players of all skill levels.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BingoPatternsPage;
